import { useHistory, useLocation } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';

import { useDynamicTablePageSize } from './invoiceUtils/useDynamicTablePageSize';
import { useInvoiceDateTable } from './invoiceUtils/useInvoiceDateTable';
import { InvoicePaths } from '../../app/enums/InvoicePaths';
import { numberOr } from '../../common/utils/numberUtils';
import { EMPTY_VALUE } from '../../common/utils/formatUtils';
import { CurrencyFormat } from '../../components/domainSpecific/currencyElements';
import DynamicTable from '../../components/tables/table';
import { useInvoiceStyles } from './invoice.styles';

const TABLE_PAGE_SEARCH_PARAM = 'tablePage';

export default function InvoiceStatementResultsDisplay({
  accountNumber,
  accountName,
  data,
  rangeEnd,
  rangeStart,
}) {
  const history = useHistory();
  const location = useLocation();
  const { styles } = useInvoiceStyles();
  const { state, search } = location || {};
  const urlSearchParams = new URLSearchParams(search);
  const currentTablePage = Number(
    urlSearchParams.get(TABLE_PAGE_SEARCH_PARAM) || 1
  );

  const handleInvoiceRowClick = record => {
    const invoiceNumber = record?.invoiceNumber;
    if (invoiceNumber) {
      const invoiceSearchParams = {
        customerId: state?.id?.number,
        invoiceNumber,
        fromInvoiceList: true,
      };
      history.push(InvoicePaths.DETAIL, invoiceSearchParams);
    }
  };

  const handleOpenAmountClick = openAmountParams => {
    history.push(InvoicePaths.TRANSACTION_HISTORY, openAmountParams);
  };

  const { invoiceDateColumns, invoiceDateTableData, handleRowClick } =
    useInvoiceDateTable(data, handleOpenAmountClick, handleInvoiceRowClick);
  const { containerRef, pageSize } = useDynamicTablePageSize();

  const handlePaginationChange = page => {
    urlSearchParams.set(TABLE_PAGE_SEARCH_PARAM, String(page));
    history.replace({
      pathname: location.pathname,
      state,
      search: urlSearchParams.toString(),
    });
  };

  return (
    <>
      <div className={styles.textContainer}>
        <div className={styles.textWrapper}>
          <div className={styles.number}>
            <span className={styles.numberText}>
              <FormattedMessage id="invoice.label" />
            </span>
            <span className={styles.numberValue}>
              {accountNumber} | {accountName}
            </span>
          </div>
          {rangeStart && rangeEnd && (
            <div className={styles.number}>
              <span className={styles.numberText}>
                <FormattedMessage id="invoice.lookupDate" />
              </span>
              <span className={styles.numberValue}>
                {(() => {
                  const rangeStartMoment = moment(rangeStart, 'YYYY/MM/DD');
                  const rangeEndMoment = moment(rangeEnd, 'YYYY/MM/DD');

                  if (rangeStartMoment.isValid() && rangeEndMoment.isValid()) {
                    return `${rangeStartMoment.format('MMM DD YYYY')} - ${rangeEndMoment.format('MMM DD YYYY')}`;
                  }
                  return '';
                })()}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className={styles.summaryBox}>
        {data[0]?.ageing?.[0] && (
          <div className={styles.summaryContainerBox}>
            {Object.entries({
              total_due: 'invoice.totalDue',
              current: 'invoice.current',
              days_31_60: 'invoice.days3160',
              days_61_90: 'invoice.days6190',
              over_90_days: 'invoice.over90Days',
            }).map(([key, messageId]) => {
              const value = data?.[0]?.ageing?.[0]?.[key];
              const invoiceData = data?.[0]?.invoiceNumber?.[0];
              const amount = numberOr(Number(value), null);
              const currencyCode = invoiceData?.currency || 'USD';

              if (amount === null) return EMPTY_VALUE;
              return (
                <div className={styles.summaryItem} key={key}>
                  <div className="title">
                    <FormattedMessage id={messageId} />
                  </div>
                  <div className="value">
                    <CurrencyFormat value={amount} currency={currencyCode} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div
        className={styles.tableContainer}
        ref={containerRef}
        data-subject="statement"
        data-role="table"
      >
        <DynamicTable
          columns={invoiceDateColumns}
          data={invoiceDateTableData}
          pagination={{
            pageSize,
            current: currentTablePage,
            onChange: handlePaginationChange,
          }}
          onRowClick={handleRowClick}
          autoClickableRows
        />
      </div>
    </>
  );
}
